import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import PersonCard from "../components/team/PersonCard";
import Accent from "../components/ui/Accent";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import {
  aboutCta,
  aboutHero,
  aboutStats,
  awards,
  awardsIntro,
  mission,
  reach,
  story,
  teamIntro,
} from "../data/about";
import { images } from "../data/images";
import { teamGroups } from "../data/team";
import { photoProps } from "../lib/links";

function StatStrip() {
  return (
    <div className="border-t-[3px] border-yellow bg-ink text-white">
      <dl className="mx-auto grid max-w-280 grid-cols-2 sm:grid-cols-3">
        {aboutStats.map(({ value, label }, i) => (
          <div
            key={label}
            className={
              "flex flex-col-reverse justify-end px-4 py-6 text-center sm:border-r sm:border-white/10 sm:last:border-r-0 " +
              (i === aboutStats.length - 1
                ? "col-span-2 sm:col-span-1"
                : "border-b border-white/10 sm:border-b-0")
            }
          >
            <dt className="mt-1 text-[13.5px] leading-snug text-white/75">
              {label}
            </dt>
            <dd className="font-display text-[36px] leading-none text-yellow">
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Story() {
  return (
    <section
      id="story"
      className="bg-white px-4 py-20 sm:px-6 md:py-24"
      aria-labelledby="story-title"
    >
      <div className="on-light mx-auto max-w-280">
        <span className="eyebrow text-blue-deep">{story.eyebrow}</span>
        <h2
          id="story-title"
          className="font-display mt-4 mb-10 max-w-[22ch] text-[34px] text-slate uppercase md:text-[46px] lg:text-[52px]"
        >
          <Accent>{story.title}</Accent>
        </h2>
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16">
          <div className="space-y-5 text-[17px] leading-relaxed text-ink">
            {story.paragraphs.map((text) => (
              <p key={text.slice(0, 24)}>{text}</p>
            ))}
          </div>
          <div className="rounded-brand bg-surface p-6 sm:p-8">
            <h3 className="tag mb-6 text-[13px] font-semibold text-blue-deep">
              Two decades, one through-line
            </h3>
            <ol className="relative">
              {story.timeline.map(({ when, text }) => (
                <li
                  key={when}
                  className="relative ml-2 border-l-[3px] border-line pb-6 pl-8 last:border-l-transparent last:pb-0"
                >
                  <span
                    aria-hidden="true"
                    className="absolute top-1 -left-[9.5px] size-4 rounded-full border-[3px] border-surface bg-yellow shadow-[0_0_0_2px_var(--color-yellow)]"
                  />
                  <span className="font-display block text-[15px] tracking-[0.1em] text-blue-deep uppercase">
                    {when}
                  </span>
                  <p className="mt-1 text-[15.5px] leading-normal text-muted">
                    {text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <aside className="mt-14 grid gap-4 rounded-r-brand border-l-4 border-yellow bg-surface px-6 py-7 sm:px-9 md:grid-cols-[220px_1fr] md:gap-10">
          <h3 className="font-display text-[26px] text-slate uppercase">
            {story.callout.title}
          </h3>
          <div className="space-y-3 text-[16.5px] leading-relaxed text-ink">
            {story.callout.paragraphs.map((text) => (
              <p key={text.slice(0, 24)}>{text}</p>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

function Mission() {
  return (
    <section className="blueprint px-4 py-20 text-center text-white sm:px-6 md:py-24">
      <div className="mx-auto max-w-[880px]">
        <span className="eyebrow text-blue-soft">{mission.eyebrow}</span>
        <h2 className="font-display mt-5 text-[30px] leading-[1.15] uppercase md:text-[44px] lg:text-[48px]">
          <Accent>{mission.title}</Accent>
        </h2>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section
      id="team"
      className="bg-surface px-4 py-20 sm:px-6 md:py-24"
      aria-labelledby="team-title"
    >
      <div className="mx-auto max-w-280">
        <div className="on-light max-w-[720px]">
          <span className="eyebrow text-blue-deep">{teamIntro.eyebrow}</span>
          <h2
            id="team-title"
            className="font-display mt-4 text-[34px] text-slate uppercase md:text-[46px] lg:text-[52px]"
          >
            <Accent>{teamIntro.title}</Accent>
          </h2>
          <p className="mt-3 text-[17px] leading-relaxed text-muted">
            {teamIntro.text}
          </p>
        </div>
        {teamGroups.map((group, g) => (
          <div key={group.name} className="mt-12">
            <h3 className="mb-5 flex items-center gap-4 text-[14px] font-bold tracking-[0.14em] text-blue-deep uppercase">
              {group.name}
              <span aria-hidden="true" className="h-px flex-1 bg-line" />
            </h3>
            <ul
              className={
                g === 0
                  ? "grid grid-cols-1 gap-4 sm:grid-cols-3"
                  : "grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5"
              }
            >
              {group.people.map((person) => (
                <li key={person.path}>
                  <PersonCard person={person} featured={g === 0} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function Reach() {
  return (
    <section className="bg-white px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto max-w-280">
        <div className="grid overflow-hidden rounded-brand bg-ink text-white lg:grid-cols-[1.05fr_0.95fr]">
          <div className="flex flex-col justify-center px-6 py-9 sm:px-10 sm:py-12">
            <span className="eyebrow text-blue-soft">Home base</span>
            <h3 className="font-display mt-4 mb-4 text-[28px] leading-[1.1] uppercase md:text-[34px]">
              <Accent>{reach.home.title}</Accent>
            </h3>
            <p className="mb-7 max-w-[520px] text-[16px] text-white/80">
              {reach.home.text}
            </p>
            <div>
              <ButtonLink />
            </div>
          </div>
          <figure className="relative min-h-60 sm:min-h-80">
            <img
              {...photoProps(
                images.houghton,
                "(max-width: 1024px) 100vw, 540px",
              )}
              alt={reach.home.imageAlt}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <figcaption className="tag absolute bottom-3 left-3 rounded bg-ink/80 px-2.5 py-1.5 text-[11px] text-white/90">
              Portage Lake Lift Bridge · Houghton–Hancock
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

function Awards() {
  return (
    <section
      id="awards"
      className="border-t border-line bg-white px-4 py-20 sm:px-6 md:py-24"
    >
      <div className="mx-auto max-w-280">
        <SectionHeading eyebrow={awardsIntro.eyebrow} title={awardsIntro.title}>
          <p>{awardsIntro.text}</p>
        </SectionHeading>
        <ul className="mt-11 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {awards.map(({ year, name, image }) => (
            <li
              key={image}
              className="flex flex-col overflow-hidden rounded-brand border border-line bg-surface"
            >
              <div className="border-b border-line bg-white">
                <img
                  {...photoProps(image, "(max-width: 640px) 100vw, 270px")}
                  alt={name}
                  loading="lazy"
                  className="aspect-[2/1] w-full object-contain"
                />
              </div>
              <div className="px-5 pt-4 pb-5">
                <span className="font-display block text-[28px] leading-none text-slate">
                  {year}
                </span>
                <span className="mt-2 block text-[14px] leading-snug font-bold text-muted">
                  {name}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow={aboutHero.eyebrow}
        title={aboutHero.title}
        lede={aboutHero.lede}
        image={images.teamGroup}
        imageAlt="The Xeratec team together outside the Hancock office"
      >
        <div className="flex flex-wrap gap-3.5">
          <ButtonLink href="#team" variant="glass">
            Meet the team ↓
          </ButtonLink>
        </div>
      </PageHero>
      <StatStrip />
      <Story />
      <Mission />
      <Team />
      <Reach />
      <Awards />
      <CtaPanel title={aboutCta.title} text={aboutCta.text} />
    </>
  );
}
